import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Package, Sparkles, PlayCircle, CheckCircle2, XCircle } from "lucide-react";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["open", "in_progress", "done", "cancelled"];

const prioColor = (p) =>
  p === "urgent" ? "bg-red-100 text-red-800" :
  p === "high" ? "bg-amber-100 text-amber-800" :
  p === "medium" ? "bg-slate-100 text-slate-700" : "bg-emerald-50 text-emerald-700";
const statusColor = (s) =>
  s === "done" ? "bg-emerald-100 text-emerald-800" :
  s === "in_progress" ? "bg-blue-100 text-blue-800" :
  s === "cancelled" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700";

export function MfgOrdersTab() {
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoForm, setAutoForm] = useState({ source: "sales_order" });
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const loadRefs = async () => {
    try {
      const [b, p] = await Promise.all([api.get("/branches"), api.get("/products")]);
      setBranches(b.data);
      setProducts(p.data);
    } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (branchFilter) params.set("branch_id", branchFilter);
    if (statusFilter) params.set("status", statusFilter);
    if (sourceFilter) params.set("source", sourceFilter);
    try {
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/mfg/orders${q}`);
      setRows(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, statusFilter, sourceFilter]);

  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;

  const save = async () => {
    if (!form.branch_id || !form.product_id || !form.quantity) {
      toast.error("Factory, product and quantity are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), batch_size: form.batch_size ? Number(form.batch_size) : undefined };
      if (form.id) {
        const { id, branch_id, product_id, po_number, source, source_ref, product_name, product_sku, created_at, created_by, updated_at, ...body } = payload;
        await api.patch(`/mfg/orders/${id}`, body);
      } else {
        await api.post("/mfg/orders", payload);
      }
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const patchStatus = async (row, newStatus) => {
    try { await api.patch(`/mfg/orders/${row.id}`, { status: newStatus }); toast.success(`Marked ${newStatus.replace("_", " ")}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete order?")) return;
    try { await api.delete(`/mfg/orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runAutoGenerate = async () => {
    if (!autoForm.branch_id) { toast.error("Pick factory"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/mfg/orders/auto-generate?branch_id=${autoForm.branch_id}&source=${autoForm.source}`);
      toast.success(`${r.data.generated} order(s) generated`);
      setAutoOpen(false); setAutoForm({ source: "sales_order" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="pt-3 space-y-4" data-testid="mfg-orders-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="mfg-orders-branch-filter"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="mfg-orders-status-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Source</Label>
          <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="mfg-orders-source-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any source</SelectItem>
              {["manual", "sales_order", "forecast", "min_stock"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2 self-end">
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => { setAutoForm({ source: "sales_order", branch_id: branchFilter || "" }); setAutoOpen(true); }} data-testid="mfg-order-auto-generate">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Auto-generate
          </Button>
          <Button size="sm" className="bg-[#C05621] text-white" onClick={() => { setForm({ priority: "medium", source: "manual", branch_id: branchFilter || undefined }); setOpen(true); }} data-testid="mfg-order-new">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Order
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="mfg-orders-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">PO #</th>
              <th className="text-left px-3 py-2">Factory</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-center px-3 py-2">Source</th>
              <th className="text-center px-3 py-2">Priority</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Expected</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`mfg-order-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.po_number}</td>
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.product_name}</div><div className="text-[10px] text-[#8B7355]">{r.product_sku}</div></td>
                <td className="px-3 py-2 text-right font-mono">{r.quantity}</td>
                <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{(r.source || "manual").replace("_", " ")}</Badge></td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${prioColor(r.priority)}`}>{r.priority}</Badge></td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${statusColor(r.status)}`}>{r.status?.replace("_", " ")}</Badge></td>
                <td className="px-3 py-2 text-xs">{r.expected_finish || "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "open" && (
                    <Button size="sm" variant="outline" className="text-blue-700 border-blue-300" onClick={() => patchStatus(r, "in_progress")} data-testid={`mfg-order-start-${r.id}`}>
                      <PlayCircle className="w-3 h-3 mr-1" /> Start
                    </Button>
                  )}
                  {r.status === "in_progress" && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 ml-1" onClick={() => patchStatus(r, "done")} data-testid={`mfg-order-done-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="ml-1" onClick={() => { setForm(r); setOpen(true); }}>Edit</Button>
                  {r.status !== "cancelled" && r.status !== "done" && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => patchStatus(r, "cancelled")} title="Cancel">
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#8B7355]">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No production orders yet. Create a manual order or auto-generate from demand.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Production Order</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-order-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Source</Label>
              <Select value={form.source || "manual"} onValueChange={(v) => setForm({ ...form, source: v })} disabled={!!form.id}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["manual", "sales_order", "forecast", "min_stock"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Product*</Label>
              <Select value={form.product_id || ""} onValueChange={(v) => setForm({ ...form, product_id: v })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-order-form-product"><SelectValue placeholder="Choose product" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantity*</Label>
              <Input type="number" step="0.1" min="0" value={form.quantity || ""} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="mfg-order-form-quantity" />
            </div>
            <div><Label>Batch size</Label>
              <Input type="number" step="0.1" min="0" value={form.batch_size || ""} onChange={(e) => setForm({ ...form, batch_size: e.target.value })} />
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority || "medium"} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="mfg-order-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Expected finish</Label>
              <Input type="date" value={(form.expected_finish || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expected_finish: e.target.value })} />
            </div>
            {form.id && (
              <div className="col-span-2"><Label>Status</Label>
                <Select value={form.status || "open"} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2"><Label>Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="mfg-order-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-generate dialog */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-[Outfit]">Auto-generate Orders</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Factory*</Label>
              <Select value={autoForm.branch_id || ""} onValueChange={(v) => setAutoForm({ ...autoForm, branch_id: v })}>
                <SelectTrigger data-testid="mfg-order-auto-branch"><SelectValue placeholder="Pick factory" /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Source</Label>
              <Select value={autoForm.source} onValueChange={(v) => setAutoForm({ ...autoForm, source: v })}>
                <SelectTrigger data-testid="mfg-order-auto-source"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="sales_order">Sales Orders (unfulfilled demand, 30d)</SelectItem>
                  <SelectItem value="min_stock">Min-Stock Refill (2× min level)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-[#8B7355] bg-[#FEF9EA] border border-amber-200 p-2 rounded">
              This scans open sales demand vs on-hand stock (or products below min_stock_level) and creates one open PO per SKU with the calculated shortfall.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(false)}>Cancel</Button>
            <Button onClick={runAutoGenerate} disabled={busy} className="bg-emerald-700 text-white" data-testid="mfg-order-auto-run">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
